import React from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { isCarvanaNativeApp } from '@carvana/core-native-messaging';
import { AnalyticEvents } from '@constants/index';
import { Analytics } from '@services/analytics-service';
import { Button } from '@carvana/core-helios-button';
import { useSnapshot } from 'valtio';
import ExternalLinkIcon from '@components/service-and-repair/components/service-repair-center/icons/external-link';
import { useRepairFacilities } from '@components/service-and-repair/providers/repair-facilities';
import { sendNativeMessage, NativeMessageEvent } from '@components/_common/link/link';
import {
  isCovered100DayLimitedWarrantyAndWithinXDays,
  isCovered100DayLimitedWarrantyWithin30DaysAndInCalifornia,
  isCovered30Days,
  SarStore,
  isSubCategoryIncluded,
  isUserWithinXDays,
} from '@store/sar-store';
import { useUserSelections } from '@components/service-and-repair/providers/user-selections';
import { SubCategory } from '@components/service-and-repair/constants';
import { PreferredPartnerForm } from '../service-repair-center/preferred-partner-form';
import styles from './coverage.module.css';
import { CoveragePageProps, generateKey } from './coverage-screen';
import { useRouter } from '../../providers/router';
import { useCoverage } from './use-coverage';

export const Good = (props: CoveragePageProps) => {
  const { button, description, title, showPreferredPartnersForm, coverageScreenType, deductible } = props || {};
  const { closeModal } = useRouter();
  const { isLoading: isSearching, repairFacilities } = useRepairFacilities();
  const repairFacilitiesCount = repairFacilities?.length || 0;
  const hideTreatmentViewMoreCTA = showPreferredPartnersForm && repairFacilitiesCount > 2;
  const hideViewMoreCTA = repairFacilitiesCount > 0;
  const isButtonViewMore = button?.text?.toLowerCase()?.includes('view more');

  const primaryCtaOnClick = () => {
    if (button?.closeModal) {
      closeModal();
      return;
    }

    Analytics.trackEvent(AnalyticEvents.sarCoverageScreenClickedBottomButton, {
      buttonName: button?.text,
    });
    datadogRum.addAction(AnalyticEvents.sarCoverageScreenClickedBottomButton, {
      buttonName: button?.text,
    });

    if (isCarvanaNativeApp()) {
      sendNativeMessage('link' as NativeMessageEvent, {
        url: button?.link,
      });
    } else {
      window.open(button?.link, '_blank');
    }
  };

  return (
    <div className={styles['coverage-page-wrapper']}>
      <div className={styles['coverage-header-wrapper']}>
        <div className={styles['header']}>{title}</div>
        <div className={styles['header-paragraph-wrapper']}>
          {Array.isArray(description)
            ? description.map((desc) => (
                <p key={generateKey()} className={styles['paragraph']}>
                  {desc}
                </p>
              ))
            : description}
        </div>
      </div>

      {showPreferredPartnersForm && <PreferredPartnerForm heading="In-network shops near you" />}

      {!isSearching && (
        <>
          {!hideViewMoreCTA && isButtonViewMore && !isSearching ? (
            <div className={styles['silverrock-cta']}>
              Call SilverRock™ at <a href="tel:877-584-3848">877-584-3848</a> to find an in-network shop.
            </div>
          ) : (
            !hideTreatmentViewMoreCTA && (
              <div className={styles['coverage-button-wrapper']}>
                <Button
                  priority={button?.buttonType as any}
                  iconRight={button?.viewMoreButton && <ExternalLinkIcon />}
                  className={button?.viewMoreButton ? styles['view-more-shops'] : ''}
                  onClick={() => primaryCtaOnClick()}
                >
                  {button?.text}
                </Button>
              </div>
            )
          )}
        </>
      )}

      <Disclaimer />
    </div>
  );
};

const Disclaimer = () => {
  const { determineCoverage, userCoverageAndSelections } = useCoverage();
  const { saleEffectiveDate } = useSnapshot(SarStore);
  const { userFeedData } = useUserSelections();
  const { mileage } = userFeedData || {};
  const { deductible } = determineCoverage || {};
  const { subcategory } = userCoverageAndSelections || {};
  const isOilOrTireSubCategory = subcategory === SubCategory.OilChangeAndOrTireRotation;

  if (isOilOrTireSubCategory) {
    return (
      <div className={styles['disclaimer']}>
        <p>*Oil changes and tire rotations are covered for the first year of ownership.</p>
      </div>
    );
  }

  return (
    <div className={styles['disclaimer']}>
      {isCovered100DayLimitedWarrantyAndWithinXDays(14) && (
        <SilverRock14DaysDisclaimer purchaseDate={saleEffectiveDate} inNetworkDeductible={deductible?.inNetwork} />
      )}

      {isCovered100DayLimitedWarrantyWithin30DaysAndInCalifornia() && (
        <SilverRockInNetworkDeductible30DaysDisclaimer
          purchaseDate={saleEffectiveDate}
          inNetworkDeductible={deductible?.inNetwork}
          mileageAtPurchase={mileage || 0}
        />
      )}

      {isUserWithinXDays(14) && isCovered30Days() && isSubCategoryIncluded(subcategory) && (
        <>
          <CoverageDoestApplyDisclaimer mileageAtPurchase={mileage || 0} />

          <SilverRock14DaysDisclaimer
            purchaseDate={saleEffectiveDate}
            inNetworkDeductible={deductible?.inNetwork}
            extraAsterisk
          />
        </>
      )}

      {!isUserWithinXDays(14) && isCovered30Days() && isSubCategoryIncluded(subcategory) && (
        <CoverageDoestApplyDisclaimer mileageAtPurchase={mileage || 0} />
      )}
    </div>
  );
};

const SilverRock14DaysDisclaimer = ({
  purchaseDate,
  inNetworkDeductible,
  extraAsterisk = false,
}: {
  purchaseDate: Date | string | null;
  inNetworkDeductible: number | undefined;
  extraAsterisk?: boolean;
}) => (
  <p>
    {extraAsterisk && '*'}*You must file your claim with SilverRock™ within 14 days of your purchase date,{' '}
    {convertDateToLocaleString(purchaseDate)}, for a ${inNetworkDeductible} in-network deductible.
  </p>
);

const SilverRockInNetworkDeductible30DaysDisclaimer = ({
  purchaseDate,
  inNetworkDeductible,
  mileageAtPurchase,
}: {
  purchaseDate: Date | string | null;
  inNetworkDeductible: number | undefined;
  mileageAtPurchase: number;
}) => (
  <p>
    *For a ${inNetworkDeductible} in-network deductible, you must file your claim with SilverRock™ within 30 days of
    your purchase date, {convertDateToLocaleString(purchaseDate)}. You also cannot exceed 1,000 miles driven—your
    vehicle was purchased at {mileageAtPurchase?.toLocaleString('en-US')} miles.
  </p>
);

const CoverageDoestApplyDisclaimer = ({ mileageAtPurchase }: { mileageAtPurchase: number }) => (
  <p>
    *Coverage doesn&apos;t apply if you exceed 1,000 miles driven—your vehicle was purchased at{' '}
    {mileageAtPurchase?.toLocaleString('en-US')} miles.
  </p>
);

const convertDateToLocaleString = (date: Date | string | null) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  // should only display date
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};
